import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Typography, Slider, Space } from 'antd';
import { 
  CloseOutlined, 
  ZoomInOutlined, 
  ZoomOutOutlined, 
  RotateLeftOutlined, 
  RotateRightOutlined,
  LeftOutlined,
  RightOutlined,
  ExpandOutlined,
  CopyOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { downloadMedia, copyToClipboard } from '../utils';
import './FullScreenViewer.css';

const FullScreenViewer = ({ image, images = [], onClose }) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showImageList, setShowImageList] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0 });
  const [touchMove, setTouchMove] = useState(0);
  const [isSwipeDragging, setIsSwipeDragging] = useState(false);
  
  // Find current image index
  useEffect(() => {
    if (image && images.length > 0) {
      const index = images.findIndex(img => img.id === image.id);
      setCurrentIndex(index >= 0 ? index : 0);
    }
  }, [image, images]);

  // Prevent body scroll when viewer is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Navigation handlers with useCallback to prevent useEffect dependency issues
  const handleReset = useCallback(() => {
    setZoom(100);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handlePrevious = useCallback(() => {
    if (images.length > 1) {
      setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
      handleReset();
    }
  }, [images.length, handleReset]);

  const handleNext = useCallback(() => {
    if (images.length > 1) {
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
      handleReset();
    }
  }, [images.length, handleReset]);
  
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowLeft' && images.length > 1) {
        handlePrevious();
      } else if (e.key === 'ArrowRight' && images.length > 1) {
        handleNext();
      } else if (e.key === '+' || e.key === '=') {
        setZoom(prev => Math.min(prev + 25, 500));
      } else if (e.key === '-') {
        setZoom(prev => Math.max(prev - 25, 25));
      } else if (e.key === '0') {
        handleReset();
      } else if (e.key === 'l' || e.key === 'L') {
        setRotation(prev => prev + 90);
      } else if (e.key === 'r' || e.key === 'R') {
        setRotation(prev => prev - 90);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onClose, images.length, handleNext, handlePrevious, handleReset]);
  
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 500));
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };
  
  const handleRotateLeft = () => {
    setRotation(prev => prev - 90);
  };
  
  const handleRotateRight = () => {
    setRotation(prev => prev + 90);
  };
  
  
  const handleImageSelect = useCallback((index) => {
    setCurrentIndex(index);
    handleReset();
  }, [handleReset]);
  
  const handleDownload = async () => {
    await downloadMedia([currentImage]);
  };
  
  const handleCopyUrl = () => {
    copyToClipboard(currentImage.url);
  };
  
  const handleMouseDown = (e) => {
    if (zoom > 100) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };
  
  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -25 : 25;
    setZoom(prev => Math.max(25, Math.min(500, prev + delta)));
  };
  
  // Swipe handlers for navigation
  const handleTouchStart = (e) => {
    if (zoom === 100 && images.length > 1) {
      setTouchStart({ x: e.touches[0].clientX });
      setTouchMove(0);
      setIsSwipeDragging(true);
    }
  };
  
  const handleTouchMove = (e) => {
    if (!isSwipeDragging || zoom !== 100) return;
    
    const currentX = e.touches[0].clientX;
    const diffX = currentX - touchStart.x;
    
    e.preventDefault();
    setTouchMove(diffX);
  };
  
  const handleTouchEnd = (e) => {
    if (!isSwipeDragging) return;
    
    const threshold = 60;
    
    if (Math.abs(touchMove) > threshold) {
      if (touchMove > 0 && currentIndex > 0) {
        handlePrevious();
      } else if (touchMove < 0 && currentIndex < images.length - 1) {
        handleNext();
      }
    }
    
    setTouchMove(0);
    setIsSwipeDragging(false);
  };
  
  if (!image) return null;
  
  // Use current image from array if available
  const currentImage = images.length > 0 ? images[currentIndex] : image;
  const isVideo = currentImage.type === 'video';
  const isAudio = currentImage.type === 'audio';
  
  const hasPrevious = images.length > 1 && currentIndex > 0;
  const hasNext = images.length > 1 && currentIndex < images.length - 1;
  
  return (
    <Modal
      open={!!image}
      onCancel={onClose}
      footer={null}
      width="100vw"
      style={{ maxWidth: '100vw', top: 0, padding: 0 }}
      className="fullscreen-viewer"
      closeIcon={<CloseOutlined />}
      destroyOnClose
    >
      <div className="viewer-container">
        <div className="viewer-header">
          <div className="header-left">
            <Typography.Title level={4} className="image-title">
              {currentImage.label}
            </Typography.Title>
            <Typography.Text className="image-info">
              {zoom}% • {rotation}° rotation • {currentIndex + 1} of {images.length || 1}
            </Typography.Text>
          </div>
          
          <Space size={8}>
            {images.length > 1 && (
              <>
                <Button
                  icon={<LeftOutlined />}
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  title="Previous (Arrow Left)"
                >
                  Previous
                </Button>
                <Button
                  icon={<RightOutlined />}
                  onClick={handleNext}
                  disabled={!hasNext}
                  title="Next (Arrow Right)"
                >
                  Next
                </Button>
              </>
            )}
            <Button
              icon={<ExpandOutlined />}
              onClick={handleReset}
              title="Reset (0)"
            >
              Reset
            </Button>
            <Button
              icon={<RotateLeftOutlined />}
              onClick={handleRotateLeft}
              title="Rotate Left (L)"
            />
            <Button
              icon={<RotateRightOutlined />}
              onClick={handleRotateRight}
              title="Rotate Right (R)"
            />
            <Button
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              disabled={zoom <= 25}
              title="Zoom Out (-)"
            />
            <Slider
              value={zoom}
              onChange={setZoom}
              min={25}
              max={500}
              step={25}
              style={{ width: 150 }}
            />
            <Button
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              disabled={zoom >= 500}
              title="Zoom In (+)"
            />
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyUrl}
            >
              Copy URL
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Space>
        </div>
        
        <div 
          className="viewer-content"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ cursor: zoom > 100 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          {/* Left navigation button */}
          {images.length > 1 && currentIndex > 0 && (
            <Button
              className="nav-button nav-button-left"
              size="large"
              icon={<LeftOutlined />}
              onClick={handlePrevious}
              type="primary"
              shape="circle"
            />
          )}

          {/* Right navigation button */}
          {images.length > 1 && currentIndex < images.length - 1 && (
            <Button
              className="nav-button nav-button-right"
              size="large"
              icon={<RightOutlined />}
              onClick={handleNext}
              type="primary"
              shape="circle"
            />
          )}

          <div
            className="image-wrapper"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) translateX(${touchMove}px) scale(${zoom / 100}) rotate(${rotation}deg)`,
              transition: isSwipeDragging ? 'none' : (isDragging ? 'none' : 'transform 0.3s ease')
            }}
          >
            {isVideo ? (
              <video
                src={currentImage.url}
                className="viewer-media"
                controls
                autoPlay
                loop
              />
            ) : isAudio ? (
              <audio
                src={currentImage.url}
                className="viewer-media"
                controls
                autoPlay
                style={{ width: '80%' }}
              />
            ) : (
              <img
                src={currentImage.url}
                alt={currentImage.label}
                className="viewer-media"
                draggable={false}
              />
            )}
          </div>
        </div>
        
        {/* Image list sidebar */}
        {showImageList && images.length > 1 && (
          <div className="image-list-sidebar">
            <div className="sidebar-header">
              <Typography.Text strong>Images ({images.length})</Typography.Text>
              <Button 
                size="small" 
                onClick={() => setShowImageList(false)}
                icon={<CloseOutlined />}
              />
            </div>
            <div className="image-list">
              {images.map((img, index) => (
                <div
                  key={img.id}
                  className={`image-list-item ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => handleImageSelect(index)}
                >
                  <img src={img.url} alt={img.label} className="thumbnail" />
                  <Typography.Text ellipsis className="thumbnail-label">
                    {img.label}
                  </Typography.Text>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="viewer-footer">
          <div className="footer-left">
            <Typography.Text className="keyboard-hints">
              ESC: close • +/-: zoom • L/R: rotate • 0: reset • ←→: navigate
            </Typography.Text>
          </div>
          {images.length > 1 && (
            <Button
              size="small"
              onClick={() => setShowImageList(!showImageList)}
              type={showImageList ? "primary" : "default"}
            >
              {showImageList ? 'Hide' : 'Show'} Images
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FullScreenViewer;
